/**
 * Integrazione di una fattura di acquisto in inversione contabile.
 *
 * Su alcune fatture di acquisto il fornitore non addebita l'IVA: l'imposta la
 * deve far emergere chi riceve, emettendo un documento di integrazione. Ometterlo
 * e' una irregolarita' sanzionata (art. 6 c.9-bis D.Lgs 471/1997), e fino a oggi
 * l'operazione non era raggiungibile dall'interfaccia.
 *
 * SCELTE DI DISEGNO, dal referto di dali del 2026-08-01:
 *
 * - La card compare SOLO se il documento ha davvero righe in inversione. Su una
 *   fattura ordinaria non esiste, nemmeno disabilitata: un comando grigio su ogni
 *   scheda insegna a ignorare quella zona proprio dove serve che salti all'occhio.
 * - L'aliquota NON viene preselezionata. Un campo pre-riempito col 22% si conferma
 *   per inerzia, e sarebbe reintrodurre dall'interfaccia la scelta d'ufficio che il
 *   backend si e' vietato apposta.
 * - I numeri li calcola il SERVER. L'arrotondamento fiscale in JavaScript sbaglia
 *   i casi con la terza cifra a 5 (1,25 al 10% darebbe 0,12 invece di 0,13), e due
 *   numeri diversi fra anteprima e documento creato distruggono la fiducia.
 * - Nessun window.confirm: l'anteprima E' la revisione e il bottone finale E' la
 *   conferma. Un secondo avviso sopra sarebbe il click regalato che insegna a
 *   confermare senza leggere.
 * - Lo stato del dialogo vive nella URL, cosi' Esc e Indietro lo chiudono e un F5
 *   non riporta l'utente alla scheda muta.
 */
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  type ComboboxOption,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Separator,
  toast,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';

/** Una simulazione per aliquota, calcolata dal server. */
interface Simulazione {
  codice: string;
  descrizione: string;
  percentuale: string;
  imposta: string;
  totale: string;
}

interface RigaInversione {
  descrizione: string | null;
  natura: string | null;
  importo: string;
}

interface Anteprima {
  richiesta: boolean;
  integrazione_esistente: { id: string; numero: string | null } | null;
  nature: string[];
  imponibile_da_integrare: string;
  imponibile_documento: string;
  righe: RigaInversione[];
  aliquote: Simulazione[];
}

interface Props {
  fatturaId: string;
  year: string;
  /** Perche' il comando non e' azionabile, o null se lo e'. */
  motivoBlocco?: string | null;
  onIntegrata: () => void;
  onVaiA: (id: string) => void;
}

const fmtEUR = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
});

/** Importo che arriva dal server come stringa decimale: si formatta, non si
 *  ricalcola. Number() serve solo alla resa a schermo. */
const euro = (valore: string): string => fmtEUR.format(Number(valore));

const IntegrazioneCard: React.FC<Props> = ({
  fatturaId,
  year,
  motivoBlocco = null,
  onIntegrata,
  onVaiA,
}) => {
  const [anteprima, setAnteprima] = useState<Anteprima | null>(null);
  const [erroreAnteprima, setErroreAnteprima] = useState<string | null>(null);
  const [aperto, setAperto] = useState(false);
  const [aliquota, setAliquota] = useState<string>('');
  const [filtro, setFiltro] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);
  const [erroreDialogo, setErroreDialogo] = useState<string | null>(null);
  const [toccato, setToccato] = useState(false);

  const auth = () => ({
    Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  });

  /** Che cosa nascerebbe integrando. Non scrive nulla. */
  const caricaAnteprima = useCallback(async () => {
    setErroreAnteprima(null);
    try {
      const r = await fetch(
        `/api/v1/fatture/${fatturaId}/autofattura/anteprima?year=${year}`,
        { headers: auth() },
      );
      if (!r.ok) {
        setErroreAnteprima(
          `Non e' stato possibile leggere i dati dell'integrazione (errore ${r.status}).`,
        );
        return;
      }
      setAnteprima(await r.json());
    } catch {
      setErroreAnteprima(
        "Non e' stato possibile leggere i dati dell'integrazione: controlla la connessione.",
      );
    }
  }, [fatturaId, year]);

  useEffect(() => {
    void caricaAnteprima();
  }, [caricaAnteprima]);

  // La card non esiste se il documento non e' in inversione contabile. L'errore
  // di lettura invece si mostra: tacere renderebbe indistinguibile "non serve"
  // da "non ho potuto controllare", e sono due cose molto diverse.
  if (erroreAnteprima) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Inversione contabile</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="text-destructive">{erroreAnteprima}</span>
          <Button variant="outline" size="sm" onClick={() => void caricaAnteprima()}>
            Riprova
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (!anteprima || !anteprima.richiesta) return null;

  const integrazioneEsistente = anteprima.integrazione_esistente;
  const scelta = anteprima.aliquote.find((a) => a.codice === aliquota) ?? null;
  const opzioni: ComboboxOption[] = anteprima.aliquote
    .filter((a) => {
      const q = filtro.trim().toLowerCase();
      return !q || `${a.percentuale} ${a.descrizione}`.toLowerCase().includes(q);
    })
    .map((a) => ({
      value: a.codice,
      label: `${a.percentuale}% - ${a.descrizione}`,
      sublabel: `Imposta ${euro(a.imposta)}`,
    }));

  const chiudi = () => {
    setAperto(false);
    setErroreDialogo(null);
    setToccato(false);
  };

  const integra = async () => {
    setToccato(true);
    if (!scelta) return;
    setSalvataggio(true);
    setErroreDialogo(null);
    try {
      const r = await fetch(`/api/v1/fatture/${fatturaId}/autofattura?year=${year}`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo_documento_codice: 'TD16',
          aliquota_integrazione: scelta.percentuale,
        }),
      });
      const corpo = await r.json().catch(() => null);
      if (!r.ok) {
        // Il dialogo RESTA APERTO con la scelta dentro: un salvataggio fallito
        // non deve costringere a rifare tutto.
        setErroreDialogo(
          typeof corpo?.detail === 'string' ? corpo.detail : `Errore ${r.status}`,
        );
        return;
      }
      chiudi();
      void caricaAnteprima();
      toast.success(`Integrazione ${corpo?.numero ?? ''} creata.`, {
        description: `Imposta fatta emergere: ${euro(String(corpo?.importo_iva ?? '0'))}`,
      });
      onIntegrata();
    } catch {
      setErroreDialogo("Non e' stato possibile creare l'integrazione: controlla la connessione.");
    } finally {
      setSalvataggio(false);
    }
  };

  const bloccato = motivoBlocco !== null;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>Inversione contabile</CardTitle>
            {integrazioneEsistente ? (
              <Badge variant="success">Integrata</Badge>
            ) : (
              <Badge variant="warning">Da integrare</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Il fornitore non ha addebitato l'IVA su{' '}
            <span className="font-mono font-medium tabular-nums">
              {euro(anteprima.imponibile_da_integrare)}
            </span>{' '}
            di imponibile ({anteprima.nature.join(', ')}). L'imposta va fatta emergere
            con un documento di integrazione: ometterlo e' una irregolarita' sanzionata.
          </p>

          {integrazioneEsistente ? (
            <div className="flex flex-wrap items-center gap-2">
              <span>Integrata con il documento</span>
              <Button
                variant="link"
                className="h-auto p-0"
                onClick={() => onVaiA(integrazioneEsistente.id)}
              >
                {integrazioneEsistente.numero ?? 'apri il documento'}
              </Button>
            </div>
          ) : (
            <div className="space-y-1">
              <Button
                variant="outline"
                size="sm"
                disabled={bloccato}
                onClick={() => setAperto(true)}
              >
                Integra questa fattura
              </Button>
              {motivoBlocco && (
                /* Il motivo accanto al bottone, non in un tooltip: un
                   <button disabled> non emette eventi del mouse e il tooltip
                   non comparirebbe mai. */
                <p className="text-xs text-muted-foreground">{motivoBlocco}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={aperto} onOpenChange={(v) => (v ? setAperto(true) : chiudi())}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Integra la fattura in inversione contabile</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            <div>
              <p className="mb-1 font-medium">Righe senza imposta addebitata</p>
              <ul className="space-y-1">
                {anteprima.righe.map((r, i) => (
                  <li key={i} className="flex justify-between gap-4">
                    <span className="truncate text-muted-foreground">
                      {r.descrizione || '(senza descrizione)'}
                    </span>
                    <span className="shrink-0 font-mono tabular-nums">
                      {euro(r.importo)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {anteprima.imponibile_documento !== anteprima.imponibile_da_integrare && (
              /* Documento misto: l'integrazione porta l'imponibile INTERO ma
                 l'imposta si calcola solo sulle righe in inversione. I due
                 numeri divergono legittimamente, ma vanno visti. */
              <p className="rounded border border-amber-500/40 bg-amber-500/5 p-2 text-xs">
                Il documento ha anche righe con IVA gia' addebitata: l'imponibile
                complessivo e' {euro(anteprima.imponibile_documento)}, mentre l'imposta
                si calcola solo su {euro(anteprima.imponibile_da_integrare)}.
              </p>
            )}

            <Separator />

            <div className="space-y-1">
              <label className="font-medium" htmlFor="aliquota">
                Aliquota da applicare
              </label>
              <Combobox
                value={filtro || (scelta ? `${scelta.percentuale}% - ${scelta.descrizione}` : '')}
                onInputChange={setFiltro}
                onSelect={(opt) => {
                  setAliquota(opt.value);
                  setFiltro('');
                }}
                options={opzioni}
                placeholder="Cerca l'aliquota..."
              />
              <p className="text-xs text-muted-foreground">
                Dipende dal bene o dal servizio acquistato. Se non sei sicuro non tirare
                a indovinare: chiedi al commercialista, la fattura resta qui.
              </p>
              {toccato && !scelta && (
                <p className="text-xs text-destructive">
                  Scegli l'aliquota: senza, l'integrazione non si puo' creare.
                </p>
              )}
            </div>

            {scelta && (
              <div className="rounded border p-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Imponibile in inversione</span>
                  <span className="font-mono tabular-nums">
                    {euro(anteprima.imponibile_da_integrare)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Aliquota scelta</span>
                  <span className="font-mono tabular-nums">{scelta.percentuale}%</span>
                </div>
                <Separator className="my-2" />
                <div className="flex justify-between font-medium">
                  <span>Imposta che emerge</span>
                  <span className="font-mono tabular-nums">{euro(scelta.imposta)}</span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  A debito nel registro delle vendite e in detrazione in quello degli
                  acquisti. Verra' creato un documento nuovo in bozza, con un numero
                  della serie delle autofatture.
                </p>
              </div>
            )}

            {erroreDialogo && (
              <p role="alert" className="text-destructive">
                {erroreDialogo}
              </p>
            )}
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={chiudi} disabled={salvataggio}>
              Annulla
            </Button>
            <Button onClick={() => void integra()} disabled={salvataggio}>
              {salvataggio ? 'Creo...' : "Crea l'integrazione"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IntegrazioneCard;
